import React, {useState, useEffect} from 'react'
import { Modal } from 'react-bootstrap'
import i18n from 'live/services/i18n'
import styled from 'styled-components'
import SubmitOrCancel from 'live/components/forms/ui/submit-or-cancel'
import './style.scss'
import { Lookup } from '../../DrillDownMap'

type Props = {
    initialValues?: Lookup[]  
    lookupEntries: Lookup[]
    callback?(lookups): void
}

const MAX_LOOKUPS_TO_SELECT = 3

const ModalSettingsParameters = ({initialValues, lookupEntries = [], callback}: Props): JSX.Element => {

    const handleSubmit = (ele) => {
        const lookups = lookupEntries.filter((ele) => parametersSelected[ele.key].value)

        const paramsSaved = {}
        Object.keys(parametersSelected).forEach(key => {
            paramsSaved[key] = {...parametersSelected[key]}
        })
        
        setParametersSSaved(paramsSaved)
        callback(lookups)
        closeModal()
    }

    const handleCancel = (e) => {        
        e.preventDefault() 
        
        const aux = {}
        
        Object.keys(parametersSelected).forEach(key => {
            aux[key] = {...parametersSaved[key]}
        })
        
        setParametersSelected(aux)
        closeModal()
    }

    const handleChangeCheckbox = (key) => {
        let aux = {...parametersSelected}
        if(aux[key]?.value){
            aux[key].value = !aux[key].value
        } 
        else{
            aux[key].value = true
        } 

        setParametersSelected(aux)
    }

    const closeModal = () => {        
        setShow(false)
    }

    const fillCheckboxWithDisabled = (parameters = {}, sumFilled): void => {        
        Object.keys(parameters).forEach(key => {
            const ele = parameters[key]

            const maxFilled = sumFilled > MAX_LOOKUPS_TO_SELECT - 1
            
            if(maxFilled){                
                ele.disabled = ele.value === true ? false : true
            }
            else{
                ele.disabled = false
            }

            return ele
        })
    }

    const countCheckbkoxSelect = (parameters = {}) => {
        let sumFilled = 0;

        Object.keys(parameters).forEach(key => {
            const ele = parameters[key]
            if(ele.value) sumFilled++
        })

        return sumFilled
    }

    const initParametersSelected = (lookupsEntries, lookupsActive) => {
        const aux = {}

        lookupsEntries.forEach((ele) => {
            aux[ele.key] = {value: false, disabled: false}
        })

        lookupsActive.forEach((ele) => {
            const param = aux[ele.key]
            if(param) param.value = true  
        })

        return aux
    }

    const openModal = () => {        
        setShow(true)
    }

    const fillObj = (curr, obj = {}) => {
        if(!curr) curr = {}

        Object.keys(obj).forEach(key => {
            const {value, disabled} = obj[key]
            curr[key] = {value, disabled }
        })

        return {...curr}
    }

    const [show, setShow] = useState(false)    
    const [sumCheckboxFilled, setSumCheckboxFilled] = useState(0)
    const [parametersSelected, setParametersSelected] = useState({})
    const [parametersSaved, setParametersSSaved] = useState({})

    useEffect(() => {
        if(initialValues && initialValues.length > 0){
            const initialParameters = initParametersSelected(lookupEntries, initialValues)     
            setParametersSSaved(curr => fillObj(curr, initialParameters))
            setParametersSelected(curr => fillObj(curr, initialParameters))
        }
    }, [lookupEntries, initialValues])
    

    useEffect(() => { 
        const sumFilled = countCheckbkoxSelect(parametersSelected);
        fillCheckboxWithDisabled(parametersSelected, sumFilled)
        setSumCheckboxFilled(sumFilled)
    }, [parametersSelected])

    return <>
                <BtnSettings >
                    <div data-testid="open-modal-setting-parameters" style={{position: "absolute"}} onClick={openModal}>
                        <i className='far fa-cog'></i>
                        <span data-testid="test-btn-settings">{i18n('Settings')}</span>
                    </div>
                </BtnSettings>

                <Modal data-testid="test-modal" className='modal-settings-parameters' show={show} onHide={handleCancel}>
                    <Modal.Header closeButton>
                        <CustomModalTitle>
                            <>
                                <i className='far fa-info-circle' />
                                <span data-testid="test-modal-title">{i18n("Parameters Settings")}</span>
                            </>
                        </CustomModalTitle>
                    </Modal.Header>

                    <CustomBody>
                        <WrapperCheckbox>
                            {
                                lookupEntries?.map((entrie, index) => {
                                    const {key} = entrie;

                                    return <ContentCheckbox key={index}>
                                        <Checkbox
                                            data-testid={"parameters-value-" + index}
                                            disabled={parametersSelected[key]?.disabled} type="checkbox" 
                                            value={parametersSelected[key]?.value}
                                            checked={parametersSelected[key]?.value === true}
                                            onChange={(e) => handleChangeCheckbox(key)} /> 
                                        <label data-testid={"parameters-title-" + index}>{key}</label>
                                    </ContentCheckbox>
                                })
                            }
                        </WrapperCheckbox>

                        {
                            sumCheckboxFilled > MAX_LOOKUPS_TO_SELECT - 1 &&
                            <MsgNotification>
                                <i className='far fa-exclamation-triangle'></i>
                                {i18n("You've selected the maximum number of parameters")}
                            </MsgNotification>
                        }

                        {
                            sumCheckboxFilled === 0 &&
                            <MsgNotification>
                                <i className='far fa-exclamation-triangle'></i>
                                {i18n("You need to have at least 1 parameter selected")}
                            </MsgNotification>
                        }
                        
                        
                    </CustomBody>
                    
                    <Modal.Footer>
                        <SubmitOrCancel                        
                            cancelHref=""
                            cancelText=""
                            className=""
                            saving={false}
                            disabled={sumCheckboxFilled === 0}
                            onCancel={handleCancel}
                            onSubmit={handleSubmit}                
                            type="button"
                        />
                    </Modal.Footer>
                </Modal>
    </>
}

export default React.memo(ModalSettingsParameters)

const CustomModalTitle = styled(Modal.Title)`

    color: ${({theme}) => theme['gray-800']};
    font-size: 18px;

    i{
        font-size: 15px;
        margin-right: 7px;
        color: ${({theme}) => theme['blue-500']};
    }
    
`

const CustomBody = styled(Modal.Body)`
    padding: 0;
    background: ${({theme}) => theme['gray-0']} !important;    
`

const WrapperCheckbox = styled.div`
    padding: 16px 26px 0 26px;
    height: 216px;
    overflow-y: auto;
    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme['gray-500']};
        border-radius: 10px;
    }
`

const ContentCheckbox = styled.div`
    height: 24px;
    display: flex;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 16px;
    color: ${({theme}) => theme['gray-800']} ;
`



const Checkbox = styled.input`
    width: 16px !important;
    height: 16px !important;
    padding: 0px 3px !important;
    justify-content: center !important;
    border-radius: 2.25px !important;
    margin-right: 8px !important;
    border: none !important;
    align-items: center !important;
    display: flex !important;
    
    background: ${({theme}) => theme['blue-500']} !important;

    &::after{
        color: ${({theme}) => theme['gray-0']} !important;
    }
`

const MsgNotification = styled.div`
    font-size: 13px;
    font-weight: 600;
    height: 40px;    
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({theme}) => theme['yellow-700']};
    background: #FEF3E1;

    i{
        margin-right: 8px;
    }
`

const BtnSettings = styled.div`
    flex: 1;
    color: ${({theme}) => theme['blue-500']}
`
